import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from "motion/react";
import type { PointerEvent, ReactNode } from "react";

/**
 * A panel that tilts toward the pointer, with the light following it.
 *
 * The tilt alone is the cheap version of this effect and it looks like a CSS
 * demo. What sells it as a physical object is that the *specular highlight
 * moves with the pointer too* — a real surface catching real light. Tilt
 * without moving light reads as a rotating rectangle; both together read as
 * something lying on a table under a lamp.
 *
 * Springs rather than transitions, so the panel keeps travelling for a beat
 * after the pointer stops and settles instead of halting. That overshoot is
 * the entire difference between "animated" and "physical".
 *
 * Three things it deliberately does not do:
 *
 *   **It does not tilt on keyboard focus.** Focus gets the border and the
 *   bloom, not the rotation. A card that rotates when you Tab to it is
 *   disorienting, and rotated text is harder to read for exactly the people
 *   most likely to be navigating by keyboard.
 *
 *   **It does not tilt on touch.** `pointerType` is checked. On a phone the
 *   effect would fire on tap, at the moment the visitor is trying to read.
 *
 *   **It does not tilt under `prefers-reduced-motion`.** Vestibular triggers
 *   are exactly this: unrequested 3D rotation tied to cursor movement.
 */

/** Degrees at the extremes. Past ~10 the text starts to distort. */
const MAX_TILT = 7;
const SPRING = { stiffness: 170, damping: 18, mass: 0.6 } as const;

export function TiltPanel({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  const reduced = useReducedMotion() ?? false;

  // -0.5 … 0.5, pointer position relative to the panel's centre.
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  // 0 … 1, drives the highlight's opacity so it fades rather than snapping.
  const lit = useMotionValue(0);

  const sx = useSpring(px, SPRING);
  const sy = useSpring(py, SPRING);
  const glow = useSpring(lit, { stiffness: 120, damping: 24 });

  const rotateY = useTransform(sx, [-0.5, 0.5], [-MAX_TILT, MAX_TILT]);
  const rotateX = useTransform(sy, [-0.5, 0.5], [MAX_TILT, -MAX_TILT]);

  // The highlight is a radial gradient positioned in percent, driven by the
  // same springs as the rotation so light and geometry never desynchronise.
  const glareX = useTransform(sx, [-0.5, 0.5], ["0%", "100%"]);
  const glareY = useTransform(sy, [-0.5, 0.5], ["0%", "100%"]);
  const glare = useMotionTemplate`radial-gradient(46% 46% at ${glareX} ${glareY}, color-mix(in oklab, var(--color-accent) 26%, transparent), transparent 72%)`;

  function handleMove(event: PointerEvent<HTMLDivElement>) {
    if (reduced || event.pointerType === "touch") return;
    const box = event.currentTarget.getBoundingClientRect();
    px.set((event.clientX - box.left) / box.width - 0.5);
    py.set((event.clientY - box.top) / box.height - 0.5);
    lit.set(1);
  }

  function handleLeave() {
    px.set(0);
    py.set(0);
    lit.set(0);
  }

  return (
    <div className={`tilt-stage h-full ${className}`}>
      <motion.div
        onPointerMove={handleMove}
        onPointerLeave={handleLeave}
        style={reduced ? undefined : { rotateX, rotateY }}
        className="glass glass-sheen glass-edge tilt-panel group relative h-full rounded-xl transition-colors duration-300 focus-within:border-accent-dim hover:border-accent-dim"
      >
        {/* The moving highlight. Behind the content, above the surface, and
            aria-hidden — it is light, not information. */}
        <motion.span
          aria-hidden="true"
          style={
            reduced ? undefined : { backgroundImage: glare, opacity: glow }
          }
          className="pointer-events-none absolute inset-0 rounded-xl opacity-0"
        />
        {/* Bloom under the panel on hover. On near-black a dark shadow is
            invisible; light is the only direction that reads. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -inset-px -z-10 rounded-xl opacity-0 shadow-[0_28px_70px_-32px_var(--color-accent)] transition-opacity duration-300 group-hover:opacity-100 group-focus-within:opacity-100"
        />
        <div className="relative h-full">{children}</div>
      </motion.div>
    </div>
  );
}
